(() => {
  'use strict';
  // Only Uptain's newsletter widget is guarded; consent and legal controls stay untouched.
  const ROOT = '.__up_optin', CONTENT = '.__up_optin-content';
  const panels = new Map(), roots = new WeakMap();
  const labels = [
    ['button[data-up-event="open-form"]', 'Newsletter öffnen'],
    ['button.__up_optin-close-btn, button.__up_optin-hide-btn-mobile', 'Newsletter schließen'],
    ['button.__up_optin-submit-btn', 'Zum Newsletter anmelden']
  ];
  const SETTLE_MS = 1500;
  let outsideFocus = document.activeElement;
  const onScreen = element => {
    if (!element?.isConnected) return false;
    const box = element.getBoundingClientRect();
    if (!box.width || !box.height || box.right <= 1 || box.left >= innerWidth - 1 ||
        box.bottom <= 1 || box.top >= innerHeight - 1) return false;
    for (let node = element; node; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (node.hidden || style.visibility === 'hidden' || style.display === 'none' || Number(style.opacity) === 0) return false;
    }
    return true;
  };
  const visible = element => onScreen(element) && !element.closest('[inert],[hidden]');
  const access = element => ({inert:element.getAttribute('inert'),aria:element.getAttribute('aria-hidden')});
  function restoreAttribute(element, name, value) {
    if (value === null) element.removeAttribute(name);
    else element.setAttribute(name, value);
  }
  function restoreAccess(element, original) {
    restoreAttribute(element, 'inert', original.inert);
    restoreAttribute(element, 'aria-hidden', original.aria);
  }
  function block(record, part, blocked) {
    if (record[part + 'Blocked'] === blocked) return;
    record[part + 'Blocked'] = blocked;
    const element = record[part];
    if (blocked) {
      element.inert = true;
      element.setAttribute('aria-hidden', 'true');
    } else restoreAccess(element, record[part + 'Access']);
  }
  document.addEventListener('focusin', event => {
    if (event.target instanceof Element && !event.target.closest(ROOT)) outsideFocus = event.target;
  });
  function restoreFocus(record, allowOpener) {
    const opener = allowOpener && record.openers.find(visible);
    if (opener) { opener.focus({preventScroll:true}); return; }
    const usable = element => visible(element) && !record.root.contains(element) && element.tabIndex >= 0 && !element.disabled;
    if (usable(outsideFocus)) { outsideFocus.focus({preventScroll:true}); return; }
    // Only a fallback; do not mutate any other widget or legal control.
    const target = [...document.querySelectorAll('footer.footer .policies a, #MainContent a[href], header a[href]')].find(usable);
    if (target) target.focus({preventScroll:true});
    else document.activeElement?.blur();
  }
  function valid(record) {
    return !record.disposed && record.root.isConnected && record.content.isConnected &&
      record.content.parentElement === record.root && record.root.matches(ROOT) && record.content.matches(CONTENT);
  }
  function restoreLabel(button, original) {
    // Do not undo a newer label installed by the app itself.
    if (button.getAttribute('aria-label') === original.owned) restoreAttribute(button, 'aria-label', original.value);
  }
  function refreshControls(record) {
    const current = new Map();
    for (const [selector, name] of labels) {
      record.root.querySelectorAll(selector).forEach(button => {
        if (button.closest(ROOT) !== record.root) return;
        current.set(button, name);
        if (!record.names.has(button)) record.names.set(button, {value:button.getAttribute('aria-label'),owned:name});
        if (button.getAttribute('aria-label') !== name) button.setAttribute('aria-label', name);
      });
    }
    for (const [button, original] of record.names) {
      if (!current.has(button)) { restoreLabel(button, original); record.names.delete(button); }
    }
    record.openers = [...current.keys()].filter(button => button.matches('[data-up-event="open-form"]'));
  }
  function dispose(record) {
    if (record.disposed) return;
    record.disposed = true;
    record.observer.disconnect();
    if (record.frame) cancelAnimationFrame(record.frame);
    record.frame = 0;
    record.root.removeEventListener('click', record.click, true);
    record.root.removeEventListener('transitionend', record.transition);
    record.root.removeEventListener('transitioncancel', record.transition);
    panels.delete(record.content);
    if (roots.get(record.root) === record) roots.delete(record.root);
    restoreAccess(record.content, record.contentAccess);
    restoreAccess(record.root, record.rootAccess);
    record.names.forEach((original, button) => restoreLabel(button, original));
    record.names.clear();
    record.openers = [];
    if (outsideFocus && !outsideFocus.isConnected) outsideFocus = null;
  }
  function update(record) {
    if (!valid(record)) { dispose(record); return; }
    const contentVisible = onScreen(record.content);
    if (record.dismissing && record.root.contains(document.activeElement)) restoreFocus(record, false);
    if (!contentVisible && record.openingUntil > performance.now()) return;
    const state = contentVisible ? 'open' : record.openers.some(onScreen) ? 'collapsed' : 'fully-dismissed';
    if (state === 'fully-dismissed' && record.root.contains(document.activeElement)) restoreFocus(record, false);
    else if (state === 'collapsed' && record.content.contains(document.activeElement)) restoreFocus(record, !record.dismissing);
    block(record, 'root', state === 'fully-dismissed');
    block(record, 'content', state !== 'open');
    record.state = state;
    if (state === 'fully-dismissed') record.dismissing = false;
  }
  function settle(record) {
    if (!valid(record)) { dispose(record); return; }
    record.until = performance.now() + SETTLE_MS;
    if (record.frame) return;
    const tick = () => {
      record.frame = 0;
      update(record);
      if (!record.disposed && performance.now() < record.until) record.frame = requestAnimationFrame(tick);
    };
    record.frame = requestAnimationFrame(tick);
  }
  function discoverRoot(root) {
    if (!root?.isConnected || !root.matches(ROOT)) return;
    const content = root.querySelector(':scope > ' + CONTENT);
    const previous = roots.get(root);
    if (previous && valid(previous) && previous.content === content) return;
    if (previous) dispose(previous);
    if (!content || panels.has(content)) return;
    const record = {
      root,content,rootAccess:access(root),contentAccess:access(content),
      rootBlocked:null,contentBlocked:null,state:null,dismissing:false,disposed:false,
      names:new Map(),openers:[],frame:0,until:0,openingUntil:0,observer:null,click:null,transition:null
    };
    record.observer = new MutationObserver(mutations => {
      if (!valid(record)) { dispose(record); discoverRoot(root); return; }
      if (mutations.some(mutation => mutation.type === 'childList' || mutation.attributeName === 'class')) refreshControls(record);
      settle(record);
    });
    record.click = event => {
      if (!(event.target instanceof Element) || event.target.closest(ROOT) !== root || !valid(record)) return;
      if (event.target.closest('[data-up-event="open-form"]')) {
        record.dismissing = false;
        record.openingUntil = performance.now() + SETTLE_MS;
        block(record, 'root', false);
        block(record, 'content', false); // Allow the app's own immediate focus handler.
      } else if (event.target.closest('.__up_optin-close-btn,.__up_optin-hide-btn-mobile')) {
        record.openingUntil = 0;
        record.dismissing = !!event.target.closest('.__up_optin-close-btn');
        if (record.root.contains(document.activeElement)) restoreFocus(record, !record.dismissing);
        block(record, 'content', true);
      }
      settle(record);
    };
    record.transition = () => update(record);
    panels.set(content, record);
    roots.set(root, record);
    refreshControls(record);
    record.observer.observe(root, {attributes:true,subtree:true,attributeFilter:['class','style','hidden'],childList:true});
    root.addEventListener('click', record.click, true);
    root.addEventListener('transitionend', record.transition);
    root.addEventListener('transitioncancel', record.transition);
    update(record);
    settle(record);
  }
  function relevantNodes(node, visit) {
    if (node.nodeType !== 1) return;
    visit(node);
    node.querySelectorAll(ROOT + ', ' + CONTENT).forEach(visit);
  }
  const discovery = new MutationObserver(mutations => {
    const retired = new Set(), addedRoots = new Set();
    for (const mutation of mutations) {
      for (const node of mutation.removedNodes) relevantNodes(node, element => {
        const record = panels.get(element) || roots.get(element);
        if (record && !valid(record)) retired.add(record);
      });
      for (const node of mutation.addedNodes) relevantNodes(node, element => {
        if (element.matches(ROOT)) addedRoots.add(element);
        else if (element.matches(CONTENT) && element.parentElement?.matches(ROOT)) addedRoots.add(element.parentElement);
      });
    }
    retired.forEach(dispose); // Restore old ownership before a replacement is initialized.
    addedRoots.forEach(discoverRoot);
  });
  discovery.observe(document.documentElement, {subtree:true,childList:true});
  window.addEventListener('resize', () => panels.forEach(settle), {passive:true});
  // One bootstrap scan; subsequent discovery is limited to the changed subtrees.
  document.querySelectorAll(ROOT).forEach(discoverRoot);
})();
